// A tab left open across a deploy still references the previous build's hashed chunks, which
// no longer exist, so its next lazy route fails to load. One reload fetches the new index.html
// and its chunks. A sessionStorage flag stops a deploy that is genuinely broken from reloading
// forever: it is set before reloading and only cleared once a navigation succeeds.

import { readStorage, removeStorage, writeStorage } from "@/lib/safe-storage";

const RELOAD_FLAG_KEY = "onevault.stale-chunk-reload";

// Chrome/Edge, Firefox and Safari word a failed dynamic import differently; Vite adds the
// preload variants.
const CHUNK_LOAD_ERROR_PATTERN = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Failed to load module script/i;

let reloadPending = false;

export function isChunkLoadError(error: unknown) {
    const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
    return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

/**
 * Reloads the page unless this tab already reloaded for a stale chunk and has not loaded a
 * route successfully since. Returns whether a reload is under way.
 */
export function reloadOnceForStaleChunk() {
    if (reloadPending) {
        return true;
    }

    if (readStorage(RELOAD_FLAG_KEY, "session")) {
        return false;
    }

    writeStorage(RELOAD_FLAG_KEY, "1", "session");

    // Without storage there is nothing to stop a loop, so leave the error on screen instead.
    if (!readStorage(RELOAD_FLAG_KEY, "session")) {
        return false;
    }

    reloadPending = true;
    window.location.reload();
    return true;
}

/** Called once a navigation has loaded, so a later deploy can reload this tab again. */
export function clearStaleChunkReloadFlag() {
    if (!reloadPending && readStorage(RELOAD_FLAG_KEY, "session")) {
        removeStorage(RELOAD_FLAG_KEY, "session");
    }
}
